import {
  type ProviderEnum,
  WaasBackupOptionsEnum,
} from '@dynamic-labs/sdk-api-core';

import type { DynamicCore } from '../../../../../../client/core/types';
import { assertDefined } from '../../../../../../utils/assertDefined';
import { digestSHA256 } from '../../../../../../utils/digestSHA256';
import { encodeBase64Url } from '../../../../../../utils/encodeBase64Url';
import { randomString } from '../../../../../../utils/randomString';
import { addOAuthUrlParams } from './addOAuthUrlParams';
import { getOAuthBaseUrl } from './getOAuthBaseUrl';

const providersRequiringPkce = ['twitter', 'github'];

type OAuthUrl = {
  /** A randomly generated string for PKCE flows. */
  codeVerifier?: string;

  /** A randomly generated string for the OAuth flow. */
  state: string;

  /** The OAuth URL for the given provider. */
  url: URL;
};

/** Helper function to build the OAuth URL for a given provider. */
export const buildOAuthUrl = async (
  /** The core instance. */
  core: DynamicCore,

  /** The provider to build the OAuth URL for. */
  provider: ProviderEnum
): Promise<OAuthUrl> => {
  const { projectSettings } = core.state.get();

  // Check if the provider is supported
  assertDefined(projectSettings, 'Project settings not found');
  assertDefined(projectSettings.providers, 'Project providers not found');

  const socialProvider = projectSettings.providers.find(
    (p) => p.provider === provider
  );

  assertDefined(socialProvider, `Social provider ${provider} not supported`);

  // Check if Google Drive backup is enabled for WaaS
  const isGoogleDriveBackupEnabled =
    projectSettings.sdk.waas?.backupOptions?.includes(
      WaasBackupOptionsEnum.GoogleDrive
    );

  // Build the OAuth URL
  const loginBaseUrl = getOAuthBaseUrl(socialProvider);
  const socialProviderUrl = addOAuthUrlParams(socialProvider, loginBaseUrl, {
    isGoogleDriveBackupEnabled,
  });

  // Add PKCE and state parameters
  const usingPkce = providersRequiringPkce.includes(provider);
  const state = randomString({ length: 32 });
  const codeVerifier = randomString({ length: 43 });

  socialProviderUrl.searchParams.set('state', state);
  socialProviderUrl.searchParams.set('response_type', 'code');

  if (usingPkce) {
    const digest = await digestSHA256(codeVerifier);

    socialProviderUrl.searchParams.set(
      'code_challenge',
      encodeBase64Url(digest)
    );
    socialProviderUrl.searchParams.set('code_challenge_method', 'S256');
  }

  return {
    codeVerifier: usingPkce ? codeVerifier : undefined,
    state,
    url: socialProviderUrl,
  };
};
